import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ROUTE_META, canonicalUrl, jsonLdFor } from "./seo";

function upsert(selector: string, create: () => HTMLElement): HTMLElement {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  upsert(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute(attr, key);
    return m;
  }).setAttribute("content", content);
}

// Keeps <head> in sync with the current route during client-side navigation.
// The initial page load already has the right tags from the prerendered HTML.
export function useRouteMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const known = ROUTE_META[pathname];
    const meta = known ?? ROUTE_META["/"];

    document.title = meta.title;
    setMeta("name", "description", meta.description);
    setMeta("property", "og:title", meta.title);
    setMeta("property", "og:description", meta.description);
    setMeta("name", "twitter:title", meta.title);
    setMeta("name", "twitter:description", meta.description);

    const canonical = document.head.querySelector('link[rel="canonical"]');
    const ogUrl = document.head.querySelector('meta[property="og:url"]');
    const ld = document.head.querySelector('script[type="application/ld+json"]');

    if (known) {
      const url = canonicalUrl(known.path);
      upsert('link[rel="canonical"]', () => {
        const l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        return l;
      }).setAttribute("href", url);
      setMeta("property", "og:url", url);
      if (ld) ld.textContent = JSON.stringify(jsonLdFor(known.path));
    } else {
      // Unknown route served by the SPA fallback: don't claim a canonical.
      canonical?.remove();
      ogUrl?.remove();
      ld?.remove();
    }
  }, [pathname]);
}
